import { z } from 'zod';

// ────────────────────────────────────────────────────────────────────────────
// Server-shaped row (validated when consumed)
// ────────────────────────────────────────────────────────────────────────────

export const uiStringSchema = z.object({
  id: z.string(),
  key: z.string(),
  namespace: z.string(),
  valueEn: z.string(),
  valueHi: z.string().nullable(),
  valueTe: z.string().nullable(),
  description: z.string().nullable(),
  isActive: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type UiString = z.infer<typeof uiStringSchema>;

const uiStringTranslationSchema = z.object({
  id: z.string(),
  localeId: z.string(),
  localeCode: z.string(),
  value: z.string(),
});
export const uiStringDetailSchema = uiStringSchema.extend({
  translations: z.array(uiStringTranslationSchema),
});
export type UiStringDetail = z.infer<typeof uiStringDetailSchema>;

// ────────────────────────────────────────────────────────────────────────────
// Form schema — single dialog edits base value + all translations + flags.
// ────────────────────────────────────────────────────────────────────────────

export const uiStringFormTranslationSchema = z.object({
  localeCode: z.string().min(1),
  localeName: z.string(), // display-only; stripped before submit
  value: z.string().max(2000),
});
export type UiStringFormTranslation = z.infer<typeof uiStringFormTranslationSchema>;

export const uiStringFormSchema = z.object({
  // `key` is read-only (immutable post-create); the form surfaces it only.
  value: z
    .string()
    .min(1, 'Base value is required')
    .max(2000, 'Base value must be at most 2000 characters'),
  description: z.string().max(500, 'Description must be at most 500 characters'),
  isActive: z.boolean(),
  translations: z.array(uiStringFormTranslationSchema),
});
export type UiStringFormValues = z.infer<typeof uiStringFormSchema>;

// ────────────────────────────────────────────────────────────────────────────
// List filters
// ────────────────────────────────────────────────────────────────────────────

export const UI_STRING_SORT_KEYS = ['key', 'active', 'updated'] as const;
export type UiStringSortKey = (typeof UI_STRING_SORT_KEYS)[number];

export type SortDirection = 'asc' | 'desc';

export interface ListUiStringsParams {
  page: number;
  pageSize: number;
  searchText?: string;
  namespace?: string;
  isActive?: 'true' | 'false';
  sortBy?: UiStringSortKey;
  sortDir?: SortDirection;
}
